/**
 * MobileDeepLinkPage — per-campaign mobile deep link editor (feature #176).
 *
 * Surface:
 *   - Table of existing deep-link configs, filterable by campaign_id.
 *   - Inline create form: campaign_id, scheme, host, path_template,
 *     fallback_url, active.
 *   - Live preview: given the stored row and a context map, show the
 *     resolved deep-link URL.
 *   - Resolver simulator: POST /resolve with each of three canonical UAs
 *     (iOS Safari, Android Chrome, Desktop Firefox) and show the output.
 *   - Per-row Edit / Disable / Delete actions.
 *
 * Brand: emerald -> sky gradient header, zinc borders, 6-8 px radii,
 * light-mode only, no emojis.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	PLATFORMS,
	SAMPLE_UAS,
	buildDeepLinkPreview,
	createDeepLink,
	deepLinkFormSchema,
	deleteDeepLink,
	extractMacros,
	fetchDeepLinks,
	isValidCampaignId,
	mobileDeepLinkPaths,
	platformLabel,
	resolveDeepLink,
	updateDeepLink,
	type MobileDeepLink,
	type Platform,
	type ResolveResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const TEXTAREA = `${INPUT} min-h-[72px] font-mono`;
const HINT = "text-xs text-zinc-500";
const ERR = "text-xs text-red-600";
const CODE =
	"block whitespace-pre-wrap break-all rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-xs text-zinc-700";
const BTN =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN2 =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DEL =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const TAG_ON =
	"inline-flex items-center rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700";
const TAG_OFF =
	"inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MobileDeepLinkPageProps {
	/** When set, pre-filter the table by this campaign id. */
	initialCampaignId?: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MobileDeepLinkPage(props: MobileDeepLinkPageProps = {}) {
	const [campaignFilter, setCampaignFilter] = useState<string>(
		props.initialCampaignId ?? "",
	);
	const [rows, setRows] = useState<MobileDeepLink[]>([]);
	const [loading, setLoading] = useState(false);
	const [listError, setListError] = useState<string | null>(null);

	const appliedFilter = useMemo(() => {
		const t = campaignFilter.trim();
		if (!t) return undefined;
		return isValidCampaignId(t) ? t : undefined;
	}, [campaignFilter]);

	const load = useCallback(async () => {
		setLoading(true);
		setListError(null);
		try {
			const list = await fetchDeepLinks({ campaignId: appliedFilter });
			setRows(list);
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Failed to load deep links",
			);
		} finally {
			setLoading(false);
		}
	}, [appliedFilter]);

	useEffect(() => {
		load();
	}, [load]);

	return (
		<div className="flex flex-col gap-6">
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent">
					Mobile deep links
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Map each campaign to an in-app screen via a native URL scheme and
					path template. When a click arrives we detect the platform (iOS,
					Android, or web) and resolve the URL with the macros you provide.
					Web clicks fall through to the HTTPS fallback URL.
				</p>
				<p className={HINT}>
					API: <code className="font-mono">{mobileDeepLinkPaths.base}</code>
				</p>
			</header>

			<section
				className={`${CARD} p-4`}
				aria-labelledby="mobile-deep-link-filter"
			>
				<div className="flex flex-wrap items-end gap-3">
					<div className="flex flex-col gap-1">
						<label
							id="mobile-deep-link-filter"
							htmlFor="campaign-filter"
							className={LABEL}
						>
							Filter by campaign
						</label>
						<input
							id="campaign-filter"
							className={INPUT}
							placeholder="e.g. camp_001"
							value={campaignFilter}
							onChange={(e) => setCampaignFilter(e.target.value)}
						/>
					</div>
					<button
						type="button"
						className={BTN2}
						onClick={() => load()}
						disabled={loading}
					>
						Refresh
					</button>
					{campaignFilter && (
						<button
							type="button"
							className={BTN2}
							onClick={() => setCampaignFilter("")}
						>
							Clear
						</button>
					)}
				</div>
				{campaignFilter && !appliedFilter && (
					<p className={`${ERR} mt-2`}>
						Campaign ID must be 1-64 chars of letters, digits, or ._-
					</p>
				)}
			</section>

			<CreateForm onCreated={load} initialCampaignId={appliedFilter} />

			<ResolverSimulator defaultCampaignId={appliedFilter ?? ""} />

			<section className={CARD} aria-label="Deep link configurations">
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Configurations
					</h2>
					<span className={HINT}>
						{loading ? "Loading…" : `${rows.length} row(s)`}
					</span>
				</div>
				{listError && (
					<p className={`${ERR} px-4 py-3`}>{listError}</p>
				)}
				{!listError && rows.length === 0 && !loading && (
					<p className={`${HINT} px-4 py-6`}>
						No deep link configs yet. Create one above to get started.
					</p>
				)}
				<ul className="divide-y divide-zinc-200">
					{rows.map((row) => (
						<DeepLinkRow key={row.id} row={row} onChanged={load} />
					))}
				</ul>
			</section>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

interface CreateFormProps {
	onCreated: () => void;
	initialCampaignId?: string;
}

function CreateForm({ onCreated, initialCampaignId }: CreateFormProps) {
	const [campaignId, setCampaignId] = useState(initialCampaignId ?? "");
	const [scheme, setScheme] = useState("myapp");
	const [host, setHost] = useState("");
	const [pathTemplate, setPathTemplate] = useState("offer/{offer_id}");
	const [fallbackUrl, setFallbackUrl] = useState("https://");
	const [active, setActive] = useState(true);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	useEffect(() => {
		if (initialCampaignId && !campaignId) setCampaignId(initialCampaignId);
	}, [initialCampaignId, campaignId]);

	async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSubmitError(null);
		const parsed = deepLinkFormSchema.safeParse({
			campaignId,
			scheme,
			host,
			pathTemplate,
			fallbackUrl,
			active,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await createDeepLink(parsed.data);
			setScheme("myapp");
			setHost("");
			setPathTemplate("offer/{offer_id}");
			setFallbackUrl("https://");
			setActive(true);
			onCreated();
		} catch (err) {
			setSubmitError(
				err instanceof Error ? err.message : "Failed to create deep link",
			);
		} finally {
			setSubmitting(false);
		}
	}

	const macros = extractMacros(pathTemplate);

	return (
		<section className={`${CARD} p-4`}>
			<h2 className="mb-3 text-base font-semibold text-zinc-900">
				Create deep link
			</h2>
			<form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit}>
				<div className="flex flex-col gap-1">
					<label htmlFor="mdl-campaign" className={LABEL}>
						Campaign ID
					</label>
					<input
						id="mdl-campaign"
						className={INPUT}
						value={campaignId}
						onChange={(e) => setCampaignId(e.target.value)}
						placeholder="camp_001"
					/>
					{errors.campaignId && <p className={ERR}>{errors.campaignId}</p>}
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="mdl-scheme" className={LABEL}>
						Scheme
					</label>
					<input
						id="mdl-scheme"
						className={INPUT}
						value={scheme}
						onChange={(e) => setScheme(e.target.value)}
						placeholder="myapp"
					/>
					<p className={HINT}>
						Starts with a letter; letters, digits, + - . only. Reserved:
						javascript, data, file.
					</p>
					{errors.scheme && <p className={ERR}>{errors.scheme}</p>}
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="mdl-host" className={LABEL}>
						Host (optional)
					</label>
					<input
						id="mdl-host"
						className={INPUT}
						value={host}
						onChange={(e) => setHost(e.target.value)}
						placeholder="brand"
					/>
					{errors.host && <p className={ERR}>{errors.host}</p>}
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="mdl-path" className={LABEL}>
						Path template
					</label>
					<textarea
						id="mdl-path"
						className={TEXTAREA}
						value={pathTemplate}
						onChange={(e) => setPathTemplate(e.target.value)}
						placeholder="offer/{offer_id}"
					/>
					<p className={HINT}>
						Macros use <code className="font-mono">{"{name}"}</code>. Detected:{" "}
						{macros.length === 0 ? "(none)" : macros.join(", ")}
					</p>
					{errors.pathTemplate && (
						<p className={ERR}>{errors.pathTemplate}</p>
					)}
				</div>
				<div className="flex flex-col gap-1 md:col-span-2">
					<label htmlFor="mdl-fallback" className={LABEL}>
						Fallback URL (https required)
					</label>
					<input
						id="mdl-fallback"
						className={INPUT}
						value={fallbackUrl}
						onChange={(e) => setFallbackUrl(e.target.value)}
						placeholder="https://brand.example/offers"
					/>
					{errors.fallbackUrl && <p className={ERR}>{errors.fallbackUrl}</p>}
				</div>
				<div className="flex items-center gap-2 md:col-span-2">
					<input
						id="mdl-active"
						type="checkbox"
						className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
						checked={active}
						onChange={(e) => setActive(e.target.checked)}
					/>
					<label htmlFor="mdl-active" className={LABEL}>
						Active
					</label>
				</div>
				<div className="flex items-center gap-3 md:col-span-2">
					<button type="submit" className={BTN} disabled={submitting}>
						{submitting ? "Saving…" : "Create deep link"}
					</button>
					{submitError && <span className={ERR}>{submitError}</span>}
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
	row: MobileDeepLink;
	onChanged: () => void;
}

function DeepLinkRow({ row, onChanged }: RowProps) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [preview, setPreview] = useState<string>(
		buildDeepLinkPreview(row, sampleContextFromTemplate(row.path_template)),
	);

	useEffect(() => {
		setPreview(
			buildDeepLinkPreview(row, sampleContextFromTemplate(row.path_template)),
		);
	}, [row.scheme, row.host, row.path_template]);

	async function toggleActive() {
		setBusy(true);
		setError(null);
		try {
			await updateDeepLink(row.id, { active: !row.active });
			onChanged();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Update failed");
		} finally {
			setBusy(false);
		}
	}

	async function onDelete() {
		setBusy(true);
		setError(null);
		try {
			await deleteDeepLink(row.id);
			onChanged();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Delete failed");
		} finally {
			setBusy(false);
		}
	}

	return (
		<li className="flex flex-col gap-2 px-4 py-3 text-sm">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2">
					<span className="font-medium text-zinc-900">{row.campaign_id}</span>
					<span className="text-zinc-400">·</span>
					<span className="font-mono text-zinc-700">{row.scheme}</span>
					<span className={row.active ? TAG_ON : TAG_OFF}>
						{row.active ? "Active" : "Disabled"}
					</span>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN2}
						onClick={toggleActive}
						disabled={busy}
					>
						{row.active ? "Disable" : "Enable"}
					</button>
					<button
						type="button"
						className={BTN_DEL}
						onClick={onDelete}
						disabled={busy}
					>
						Delete
					</button>
				</div>
			</div>
			<div className="grid gap-2 md:grid-cols-2">
				<div>
					<p className={HINT}>Template</p>
					<code className={CODE}>
						{`${row.scheme}://${row.host}/${row.path_template}`}
					</code>
				</div>
				<div>
					<p className={HINT}>Preview (sample macros)</p>
					<code className={CODE}>{preview}</code>
				</div>
				<div className="md:col-span-2">
					<p className={HINT}>Fallback URL</p>
					<code className={CODE}>{row.fallback_url}</code>
				</div>
			</div>
			{error && <p className={ERR}>{error}</p>}
		</li>
	);
}

function sampleContextFromTemplate(
	template: string,
): Record<string, string> {
	const macros = extractMacros(template);
	const ctx: Record<string, string> = {};
	for (const m of macros) {
		ctx[m] = `sample_${m}`;
	}
	return ctx;
}

// ---------------------------------------------------------------------------
// Resolver simulator
// ---------------------------------------------------------------------------

interface SimulatorProps {
	defaultCampaignId: string;
}

interface SimulatorResult {
	ua: string;
	platform: Platform;
	response: ResolveResponse | null;
	error: string | null;
}

function ResolverSimulator({ defaultCampaignId }: SimulatorProps) {
	const [campaignId, setCampaignId] = useState(defaultCampaignId);
	const [context, setContext] = useState('{"offer_id": 42, "aff_id": "partner"}');
	const [results, setResults] = useState<SimulatorResult[]>([]);
	const [running, setRunning] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	useEffect(() => {
		setCampaignId(defaultCampaignId);
	}, [defaultCampaignId]);

	async function run() {
		setErr(null);
		if (!isValidCampaignId(campaignId)) {
			setErr("Enter a valid campaign ID to simulate.");
			return;
		}
		let ctx: Record<string, string | number | boolean> | undefined;
		if (context.trim()) {
			try {
				const parsed = JSON.parse(context);
				if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
					setErr("Context must be a JSON object.");
					return;
				}
				ctx = {};
				for (const [k, v] of Object.entries(
					parsed as Record<string, unknown>,
				)) {
					if (
						typeof v === "string" ||
						typeof v === "number" ||
						typeof v === "boolean"
					) {
						ctx[k] = v;
					}
				}
			} catch {
				setErr("Context must be valid JSON.");
				return;
			}
		}
		setRunning(true);
		const next: SimulatorResult[] = [];
		for (const sample of SAMPLE_UAS) {
			try {
				const response = await resolveDeepLink({
					campaignId,
					ua: sample.ua,
					context: ctx,
				});
				next.push({
					ua: sample.label,
					platform: sample.platform,
					response,
					error: null,
				});
			} catch (e) {
				next.push({
					ua: sample.label,
					platform: sample.platform,
					response: null,
					error: e instanceof Error ? e.message : "Resolve failed",
				});
			}
		}
		setResults(next);
		setRunning(false);
	}

	return (
		<section className={`${CARD} p-4`}>
			<h2 className="mb-3 text-base font-semibold text-zinc-900">
				Resolver preview
			</h2>
			<p className={`${HINT} mb-3`}>
				Simulates <code className="font-mono">{mobileDeepLinkPaths.resolve}</code>{" "}
				against the three canonical UAs below. Use it to verify the rendered
				deep-link URL matches what the app expects.
			</p>
			<div className="grid gap-3 md:grid-cols-2">
				<div className="flex flex-col gap-1">
					<label htmlFor="sim-campaign" className={LABEL}>
						Campaign ID
					</label>
					<input
						id="sim-campaign"
						className={INPUT}
						value={campaignId}
						onChange={(e) => setCampaignId(e.target.value)}
						placeholder="camp_001"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="sim-context" className={LABEL}>
						Context (JSON)
					</label>
					<textarea
						id="sim-context"
						className={TEXTAREA}
						value={context}
						onChange={(e) => setContext(e.target.value)}
					/>
				</div>
			</div>
			<div className="mt-3 flex items-center gap-3">
				<button
					type="button"
					className={BTN}
					onClick={run}
					disabled={running}
				>
					{running ? "Running…" : "Run simulation"}
				</button>
				{err && <span className={ERR}>{err}</span>}
			</div>
			{results.length > 0 && (
				<div className="mt-4 overflow-x-auto">
					<table className="w-full border-separate border-spacing-0 text-sm">
						<thead>
							<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="border-b border-zinc-200 px-3 py-2">UA</th>
								<th className="border-b border-zinc-200 px-3 py-2">Platform</th>
								<th className="border-b border-zinc-200 px-3 py-2">Deep link</th>
								<th className="border-b border-zinc-200 px-3 py-2">Fallback</th>
							</tr>
						</thead>
						<tbody>
							{results.map((r) => (
								<tr key={r.ua}>
									<td className="border-b border-zinc-100 px-3 py-2">
										{r.ua}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2">
										<span className={r.platform === "web" ? TAG_OFF : TAG_ON}>
											{platformLabel(r.response?.platform ?? r.platform)}
										</span>
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs">
										{r.error
											? `error: ${r.error}`
											: (r.response?.deep_link_url ?? "—")}
									</td>
									<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs">
										{r.response?.fallback_url ?? "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<p className={`${HINT} mt-3`}>
				Supported platforms: {PLATFORMS.map(platformLabel).join(", ")}.
			</p>
		</section>
	);
}

export default MobileDeepLinkPage;
